import { z } from 'zod'
import { parse } from 'csv-parse/sync'
import { stringify } from 'csv-stringify/sync'
import { createDebtor } from '@/shared/services/debtorServices'
import type { Company } from '@/shared/models/companyModel'
import type { Currency } from '@/shared/models/moneyModel'
import { registerCredit } from '@/modules/credits/services/creditsServices'
import { BulkDebtorRow, BulkDebtorRowSchema } from '../models/bulkUploadModels'

type BulkCreateDebtorsOptions = {
    currency?: Currency
    shouldCreateCreditLimit?: boolean
}

type RowResult = BulkDebtorRow & {
    debtorStatus: 'Success' | 'Error'
    debtorError?: string
    creditLimitStatus: 'Created' | 'Skipped' | 'Not requested' | 'Error'
    creditLimitError?: string
    debtorId?: string | null
    creditLimitId?: string | null
}

export async function bulkCreateDebtors(
    file: Blob,
    companyId: Company['id'],
    options?: BulkCreateDebtorsOptions
): Promise<Blob> {
    const csvContent = await file.text()
    const records = parse(csvContent, {
        columns: true,
        skip_empty_lines: true,
        trim: true
    }) satisfies Record<string, string>[]

    const shouldCreateCreditLimit = options?.shouldCreateCreditLimit === true

    if (shouldCreateCreditLimit && !options?.currency) {
        throw new Error('Currency is required when shouldCreateCreditLimit is true')
    }

    const getClaimedCurrency = (): Currency => {
        if (!options?.currency) {
            throw new Error('Currency is required when shouldCreateCreditLimit is true')
        }
        return options.currency
    }

    const results: RowResult[] = []

    for (const [, record] of records.entries()) {
        let rowBase: BulkDebtorRow
        let debtorId: string | null = null
        let creditLimitId: string | null = null

        let debtorStatus: RowResult['debtorStatus'] = 'Error'
        let debtorError: string | undefined

        let creditLimitStatus: RowResult['creditLimitStatus'] = shouldCreateCreditLimit ? 'Skipped' : 'Not requested'
        let creditLimitError: string | undefined

        try {
            const parsed = BulkDebtorRowSchema.parse(record)
            rowBase = parsed

            try {
                const debtorResponse = await createDebtor(
                    {
                        name: parsed.name,
                        identifier: {
                            type: parsed.identifierType,
                            value: parsed.identifierValue,
                            country: parsed.identifierCountry
                        },
                        address: {
                            first: parsed.addressFirst,
                            city: parsed.addressCity,
                            postal: parsed.addressPostal,
                            country: parsed.addressCountry
                        },
                        contacts: [
                            {
                                email: parsed.contactEmail,
                                firstName: parsed.contactFirstName,
                                lastName: parsed.contactLastName,
                                phoneNumber: parsed.contactPhoneNumber,
                                position: parsed.contactPosition
                            }
                        ]
                    },
                    companyId
                )

                debtorId = debtorResponse?.id ?? null
                debtorStatus = 'Success'
            } catch (err) {
                debtorStatus = 'Error'
                debtorError =
                    err instanceof z.ZodError
                        ? err.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ')
                        : err instanceof Error
                          ? err.message
                          : 'Unknown error while creating debtor'
            }

            if (shouldCreateCreditLimit) {
                if (debtorStatus === 'Success' && debtorId) {
                    try {
                        const creditResp = await registerCredit(
                            {
                                claimedAmount: null,
                                claimedCurrency: getClaimedCurrency(),
                                debtorId,
                                debtorIdentifier: {
                                    type: parsed.identifierType,
                                    value: parsed.identifierValue,
                                    country: parsed.identifierCountry
                                }
                            },
                            companyId
                        )

                        creditLimitId = creditResp?.id ?? null
                        creditLimitStatus = 'Created'
                    } catch (err) {
                        creditLimitStatus = 'Error'
                        creditLimitError =
                            err instanceof z.ZodError
                                ? err.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ')
                                : err instanceof Error
                                  ? err.message
                                  : 'Unknown error while creating credit limit'
                    }
                } else {
                    creditLimitStatus = 'Skipped'
                    creditLimitError = 'Skipped because debtor creation failed'
                }
            }

            results.push({
                ...rowBase,
                debtorStatus,
                debtorError,
                creditLimitStatus,
                creditLimitError,
                debtorId,
                creditLimitId
            })
        } catch (err) {
            const message =
                err instanceof z.ZodError
                    ? err.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ')
                    : err instanceof Error
                      ? err.message
                      : 'Unknown error'

            results.push({
                ...(record as BulkDebtorRow),
                debtorStatus: 'Error',
                debtorError: message,
                creditLimitStatus: shouldCreateCreditLimit ? 'Skipped' : 'Not requested',
                creditLimitError: shouldCreateCreditLimit ? 'Skipped because row validation failed' : undefined,
                debtorId: null,
                creditLimitId: null
            })
        }
    }

    const outputCsv = stringify(results, {
        header: true,
        columns: [
            ...Object.keys(BulkDebtorRowSchema.shape),
            'debtorStatus',
            'debtorError',
            'creditLimitStatus',
            'creditLimitError',
            'debtorId',
            'creditLimitId'
        ]
    })

    return new Blob([outputCsv], { type: 'text/csv;charset=utf-8' })
}
